import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Eye, Pencil, Copy, Trash2, CheckCircle2, XCircle, Loader2, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAiQuota } from '@/lib/useAiQuota';
import { AiQuotaInline } from '@/components/AiQuotaInline';

const STATUS_MAP = {
  pending_approval: { fr: 'En attente', en: 'Pending', color: 'bg-amber-500/10 text-amber-600' },
  approved: { fr: 'Approuve', en: 'Approved', color: 'bg-green-500/10 text-green-600' },
  rejected: { fr: 'Rejete', en: 'Rejected', color: 'bg-red-500/10 text-red-600' },
  draft: { fr: 'Brouillon', en: 'Draft', color: 'bg-gray-500/10 text-gray-600' },
};

export function ArticlesTab() {
  const { user } = useAuth();
  const { language: lang } = useLanguage();
  const isAdmin = ['admin', 'admin_enterprise'].includes(user?.role);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewArticle, setViewArticle] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const t = (fr, en) => lang === 'fr' ? fr : en;

  const { quota } = useAiQuota();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/seo/articles');
      setArticles(data.articles);
    } catch { toast.error(t('Erreur','Error')); }
    finally { setLoading(false); }
  };

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/seo/articles/${id}/approve`);
      setArticles(prev => prev.map(a => a.article_id === id ? data : a));
      if (viewArticle?.article_id === id) setViewArticle(data);
      toast.success(t('Article approuve','Article approved'));
    } catch { toast.error(t('Erreur','Error')); }
  };

  const reject = async (id) => {
    const reason = window.prompt(t('Raison du rejet (optionnel)','Rejection reason (optional)'));
    if (reason === null) return;
    try {
      const { data } = await api.post(`/seo/articles/${id}/reject`, { reason });
      setArticles(prev => prev.map(a => a.article_id === id ? data : a));
      toast.success(t('Article rejete','Article rejected'));
    } catch { toast.error(t('Erreur','Error')); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('Supprimer cet article?','Delete this article?'))) return;
    try {
      await api.delete(`/seo/articles/${id}`);
      setArticles(prev => prev.filter(a => a.article_id !== id));
      toast.success(t('Article supprime','Article deleted'));
    } catch { toast.error(t('Erreur','Error')); }
  };

  const startEdit = (article) => {
    setEditArticle(article);
    setEditTitle(article.title);
    setEditContent(article.content);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/seo/articles/${editArticle.article_id}`, {
        title: editTitle, content: editContent
      });
      setArticles(prev => prev.map(a => a.article_id === editArticle.article_id ? data : a));
      setEditArticle(null);
      toast.success(t('Article mis a jour','Article updated'));
    } catch { toast.error(t('Erreur','Error')); }
    finally { setSaving(false); }
  };

  const copyContent = (article) => {
    const text = `# ${article.title}\n\n${article.meta_description}\n\n${article.content.replace(/<[^>]+>/g, '')}\n\n${article.faq_schema || ''}`;
    navigator.clipboard.writeText(text);
    toast.success(t('Copie dans le presse-papiers','Copied to clipboard'));
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-articles-tab">
      <div className="flex items-center justify-end">
        <AiQuotaInline quota={quota} />
      </div>
      {articles.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p className="font-medium">{t('Aucun article','No articles')}</p>
          <p className="text-sm">{t('Generez un article depuis l\'onglet Generateur','Generate an article from the Generator tab')}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {articles.map(a => {
            const st = STATUS_MAP[a.status] || STATUS_MAP.draft;
            return (
              <Card key={a.article_id} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{a.title}</h3>
                        <Badge className={`text-xs shrink-0 ${st.color}`}>{lang === 'fr' ? st.fr : st.en}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span>{new Date(a.created_at).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA')}</span>
                        <span>-</span>
                        <Badge variant="outline" className="text-xs">{a.keyword}</Badge>
                        <span>-</span>
                        <span>{a.word_count} {t('mots','words')}</span>
                      </div>
                      {a.rejection_reason && <p className="text-sm text-red-500 mt-1">{t('Raison','Reason')}: {a.rejection_reason}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewArticle(a)} data-testid={`view-article-${a.article_id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(a)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyContent(a)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          {a.status === 'pending_approval' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => approve(a.article_id)} data-testid={`approve-article-${a.article_id}`}>
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => reject(a.article_id)}>
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(a.article_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Article Dialog */}
      <Dialog open={!!viewArticle} onOpenChange={() => setViewArticle(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="article-preview-dialog">
          {viewArticle && (() => {
            // -- Helpers (scoped to the modal) ------------------------------
            const copy = async (text, label) => {
              try {
                await navigator.clipboard.writeText(text || '');
                toast.success(t(`${label} copie`, `${label} copied`));
              } catch {
                toast.error(t('Echec de la copie', 'Copy failed'));
              }
            };
            // Pull the LAST <p>...</p> out of the article HTML so we can
            // render the CTA in its own visually distinct block. If no
            // trailing <p> is found, the article keeps its content intact
            // and the CTA block is skipped.
            const rawHtml = viewArticle.content || '';
            const ctaRegex = /<p\b[^>]*>([\s\S]*?)<\/p>\s*$/i;
            const ctaMatch = rawHtml.match(ctaRegex);
            const ctaHtml = ctaMatch ? ctaMatch[1] : '';
            const bodyHtml = ctaMatch ? rawHtml.slice(0, ctaMatch.index) : rawHtml;
            const titleSeo = viewArticle.title || '';
            const metaDesc = viewArticle.meta_description || '';
            const h1 = viewArticle.h1 || titleSeo;

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t('Apercu SEO', 'SEO preview')}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">

                  {/* TITLE SEO BLOCK */}
                  <section data-testid="preview-title-seo" className="rounded-lg border bg-muted/30 p-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                        {t('Title SEO', 'SEO title')}
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">{titleSeo.length} {t('car.', 'chars')}</span>
                    </div>
                    <p className="text-base font-semibold leading-snug">{titleSeo}</p>
                  </section>

                  {/* META DESCRIPTION BLOCK */}
                  <section data-testid="preview-meta-description" className="rounded-lg border bg-muted/30 p-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                        {t('Meta description', 'Meta description')}
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular-nums">{metaDesc.length} {t('car.', 'chars')}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground italic">{metaDesc || t('(vide)', '(empty)')}</p>
                  </section>

                  {/* ARTICLE BLOCK */}
                  <section data-testid="preview-article" className="rounded-lg border p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                        {t('Article', 'Article')}
                      </span>
                      {viewArticle.word_count ? (
                        <span className="text-[11px] text-muted-foreground tabular-nums">{viewArticle.word_count} {t('mots', 'words')}</span>
                      ) : null}
                    </div>
                    {/* H1 explicitly rendered as a distinct visual line on top */}
                    <h1 className="font-heading text-2xl font-bold tracking-tight mb-4 leading-tight" data-testid="preview-h1">
                      {h1}
                    </h1>
                    <div
                      className="prose prose-sm max-w-none prose-headings:font-heading prose-h2:mt-6 prose-h2:mb-2 prose-h2:text-xl prose-h2:font-semibold prose-h3:text-base prose-h3:font-semibold prose-p:my-2 prose-p:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml) }}
                    />
                  </section>

                  {/* FAQ BLOCK */}
                  {viewArticle.faq?.length > 0 && (
                    <section data-testid="preview-faq" className="rounded-lg border p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">FAQ</span>
                        <span className="text-[11px] text-muted-foreground tabular-nums">{viewArticle.faq.length} {t('questions', 'questions')}</span>
                      </div>
                      <div className="space-y-4">
                        {viewArticle.faq.map((f, i) => (
                          <div key={`faq-${f.question?.slice(0,32) || i}`} className="border-l-2 pl-3 border-primary/40">
                            <p className="font-semibold text-sm leading-snug">
                              <span className="text-muted-foreground mr-1.5">{i + 1}.</span>
                              {f.question}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.answer}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* CTA FINAL BLOCK */}
                  {ctaHtml && (
                    <section
                      data-testid="preview-cta"
                      className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5"
                    >
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-2">
                        {t('Appel a l\'action final', 'Final call to action')}
                      </div>
                      <div
                        className="prose prose-sm max-w-none font-medium prose-p:my-1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ctaHtml) }}
                      />
                    </section>
                  )}

                  {/* SEO PARAMETERS BLOCK (copy actions) */}
                  <section data-testid="preview-seo-params" className="rounded-lg border bg-muted/20 p-5">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                      {t('Parametres SEO', 'SEO parameters')}
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium">{t('Title SEO', 'SEO title')}</label>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => copy(titleSeo, t('Title SEO', 'SEO title'))}
                            data-testid="copy-title-seo"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            {t('Copier', 'Copy')}
                          </Button>
                        </div>
                        <p className="text-sm rounded border bg-background px-3 py-2 font-mono break-all">{titleSeo}</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium">{t('Meta description', 'Meta description')}</label>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => copy(metaDesc, t('Meta description', 'Meta description'))}
                            data-testid="copy-meta-description"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            {t('Copier', 'Copy')}
                          </Button>
                        </div>
                        <p className="text-sm rounded border bg-background px-3 py-2 font-mono break-all">{metaDesc || t('(vide)', '(empty)')}</p>
                      </div>
                    </div>
                  </section>
                </div>

                {isAdmin && viewArticle.status === 'pending_approval' && (
                  <DialogFooter>
                    <Button variant="outline" className="text-red-500" onClick={() => { reject(viewArticle.article_id); setViewArticle(null); }}>
                      <XCircle className="mr-2 h-4 w-4" />{t('Rejeter','Reject')}
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={() => { approve(viewArticle.article_id); setViewArticle(null); }}>
                      <CheckCircle2 className="mr-2 h-4 w-4" />{t('Approuver','Approve')}
                    </Button>
                  </DialogFooter>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit Article Dialog */}
      <Dialog open={!!editArticle} onOpenChange={() => setEditArticle(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t('Modifier l\'article','Edit article')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('Titre','Title')}</label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium">{t('Contenu (HTML)','Content (HTML)')}</label>
              <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={15} className="font-mono text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditArticle(null)}>{t('Annuler','Cancel')}</Button>
            <Button onClick={saveEdit} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />}
              {t('Enregistrer','Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
